import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  {
    src: '/vea-island.jpg',
    caption: 'An Island Theme',
    location: 'Palm Trees, Purple Linen, Wildflowers',
  },
  {
    src: '/vea-atrium.jpg',
    caption: 'The Grand Atrium',
    location: 'Brick Walls, Royal Blue, Sunflowers',
  },
  {
    src: '/vea-red-black.jpg',
    caption: 'Red & Black',
    location: 'Tulip Towers, Black Velvet, Scarlet Linen',
  },
  {
    src: '/vea-showgirls.jpg',
    caption: 'A Vegas Showcase',
    location: 'Feathered Headdresses, Crystal Stage',
  },
  {
    src: '/vea-podium.jpg',
    caption: 'The Awards Are Given',
    location: 'Lucite Podium, Wholesale Markets Group',
  },
];

const VerizonAwardsStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="The Verizon Excellence Awards — 32 Cities, One Vision | Dion Magee Events"
        description="A multi-year national production for Verizon Wholesale Markets — 32 identical award galas executed across the United States with custom themes, decor, lighting, entertainment, and marketing. Produced by Sir Dion Magee."
        path="/portfolio/the-verizon-excellence-awards"
        image="/vea-atrium.jpg"
        type="article"
        article={{
          headline: 'The Verizon Excellence Awards — 32 Cities, One Vision',
          datePublished: '2010-06-15',
          section: 'Corporate Events',
        }}
      />

      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/vea-atrium.jpg"
          alt="The Verizon Excellence Awards — Grand Atrium Ceremony"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />

        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Corporate Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Verizon Excellence Awards
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-xs sm:text-sm tracking-[0.3em] uppercase mt-6 max-w-3xl">
            Verizon Wholesale Markets &nbsp;&middot;&nbsp; 32 Cities &nbsp;&middot;&nbsp; A National Production
          </p>
          <p className="text-white/55 text-[10px] tracking-[0.4em] uppercase mt-3">
            Headquarters: New York, NY
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 3 chapters */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              One Vision, Many Cities
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {galleryPhotos.slice(0, 3).map((p, i) => (
              <figure
                key={i}
                data-testid={`vea-photo-${i}`}
                onClick={() => lightbox.openAt(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 1).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* VIEW STORY CTA */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">32 Events &middot; One Vision</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step inside the production that took a national awards program on the road.
          </h3>

          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[7000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Excellence, On The Road
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          {/* Pull-quote stat band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1a1a1a] border border-[#c9a96e]/15 rounded-sm overflow-hidden mb-14">
            {[
              { stat: '32', label: 'Cities, Identical Events' },
              { stat: '6,000+', label: 'Nominations Received' },
              { stat: '100%', label: 'Increase Year-Over-Year' },
              { stat: '1', label: 'Singular Brand Vision' },
            ].map((s, i) => (
              <div key={i} className="bg-[#0a0a0a] px-6 py-7 text-center">
                <p
                  className="font-serif italic text-[#e8d5a8] text-4xl md:text-5xl mb-2"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {s.stat}
                </p>
                <p className="text-white/60 text-[10px] tracking-[0.3em] uppercase">{s.label}</p>
              </div>
            ))}
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="vea-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              Staging a spectacular event is a feat in itself. Orchestrating events in
              multiple locations &mdash; at the same time &mdash; can be challenging, but not
              impossible. Taking corporate awards on the road is, we believe, one of the
              most powerful ways to connect with customers, colleagues, and the
              communities a company serves. The Verizon Excellence Awards taught us
              exactly how.
            </p>

            <p>
              The Verizon Excellence Awards honor extraordinary achievements with a
              new theme each year. Working with Verizon Wholesale Markets, our team
              created the visual language that turned every theme into a fully
              realized world &mdash; from tropical island ballrooms to Vegas-style
              showcases, from grand atriums dressed in royal blue to intimate
              red-and-black dinners centered around towering tulip arrangements.
            </p>

            <p>
              Magee then managed the program&rsquo;s digital footprint &mdash; the internet
              sites, the marketing campaign, and the special newsletters dedicated to
              the awards. We introduced signature posters and flyers, branded
              promotional collateral, and a refreshed visual identity that traveled
              with the program from city to city. Print, digital, and broadcast all
              spoke with a single voice.
            </p>

            <p>
              The most consequential redesign was the nomination tool. Together with
              our team, Magee re-architected the online application used to submit
              VEA nominations &mdash; simpler, faster, mobile-aware. The results were
              immediate: <span className="text-[#e8d5a8]">more than 6,000 nominations</span> were received,
              a <span className="text-[#e8d5a8]">100% increase</span> over previous years. Excellence,
              it turned out, only needed an easier door.
            </p>

            <p>
              Next came the part most producers dread &mdash; and the part we built our
              business on. Magee and his team designed and executed <span className="text-[#e8d5a8]">32 events
              that were exactly alike</span>. A logistical landmine at times, the project
              required securing identical decor, floral arrangements, lighting,
              entertainment, and audio-visual treatments for each of the events,
              across every market.
            </p>

            <p>
              From the palm-lined ballrooms of the South to the Manhattan atriums of
              the Northeast, every Verizon Excellence Awards looked &mdash; and felt &mdash;
              like the same evening. The lucite podium gleamed identically in
              Atlanta, Dallas, and New York. The first sunflower of the night was
              cut at the exact same height. When a recipient was called to the
              stage, the music, the lighting cue, and the engraved plaque waited
              for them in precisely the way it had waited for the recipient one
              city earlier, and the one one city later.
            </p>

            <p>
              That, in the end, is what corporate productions do at their best.
              They take a brand&rsquo;s belief in its people and translate it into a
              room that everyone leaves a little taller. The Verizon Excellence
              Awards traveled to 32 cities. The standing ovations did not change.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; Excellence isn&rsquo;t a stage. It&rsquo;s a standard, faithfully reproduced.
            </p>
          </div>

          {/* BOTTOM GALLERY - 2 chapters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-20 max-w-4xl mx-auto">
            {galleryPhotos.slice(3).map((p, i) => (
              <figure
                key={i + 3}
                data-testid={`vea-photo-${i + 3}`}
                onClick={() => lightbox.openAt(i + 3)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 4).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Plan Your Multi-City Production
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="the-verizon-excellence-awards" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default VerizonAwardsStory;
